import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { isDeviceDomain } from '@/config/api';
import Dashboard from '@/pages/Dashboard';
import SeniorInterface from '@/pages/SeniorInterface';
import UserManagement from '@/pages/UserManagement';
import HouseholdOverview from '@/pages/HouseholdOverview';
import HouseholdDetails from '@/pages/HouseholdDetails';
import SeniorProfileEditor from '@/pages/SeniorProfileEditor';
import FamilyUsers from '@/pages/FamilyUsers';
import ActivityFeed from '@/pages/ActivityFeed';
import SeniorReligionSettings from '@/pages/SeniorReligionSettings';
import DeviceHome from '@/pages/DeviceHome';
import DeviceRegistration from '@/pages/DeviceRegistration';
import NotificationSettings from '@/pages/NotificationSettings';
import NotificationEvents from '@/pages/NotificationEvents';
import { Toaster } from '@/components/ui/sonner';
import UpdateNotification from '@/components/UpdateNotification';

// Phase 11: Family Web-App
import { FamilyAuthProvider } from '@/contexts/FamilyAuthContext';
import { DeviceSettingsProvider } from '@/contexts/DeviceSettingsContext';
import { AudioPlayerProvider } from '@/contexts/AudioPlayerContext';
import FamilyLogin from '@/pages/family/FamilyLogin';
import FamilyLayout from '@/components/family/FamilyLayout';
import FamilyDashboard from '@/pages/family/FamilyDashboard';
import FamilyModules from '@/pages/family/FamilyModules';
import FamilySenioren from '@/pages/family/FamilySenioren';
import FamilySeniorDetail from '@/pages/family/FamilySeniorDetail';
import FamilyActivity from '@/pages/family/FamilyActivity';
import FamilyNotifications from '@/pages/family/FamilyNotifications';
// Phase 12: Video Call
import FamilyVideoCall from '@/pages/family/FamilyVideoCall';
import FamilyIncomingVideoCall from '@/pages/family/FamilyIncomingVideoCall';

// Admin Management (Legacy)
import MemberManagement from '@/pages/family/MemberManagement';
// Phase 16: Family Setup
import FamilyInvitePage from '@/pages/family/FamilyInvitePage';
import FamilyInviteAcceptPage from '@/pages/family/FamilyInviteAcceptPage';
import HouseholdSettingsPage from '@/pages/family/HouseholdSettingsPage';
import SeniorContactsPage from '@/pages/family/SeniorContactsPage';
import ContactsManagementPage from '@/pages/family/ContactsManagementPage';
// New Admin Pages (Phase 19: Admin Refactoring)
import RequireAdmin from '@/components/RequireAdmin';
import MembersManagement from '@/pages/family/admin/MembersManagement';
import InviteMember from '@/pages/family/admin/InviteMember';
import SeniorsManagement from '@/pages/family/admin/SeniorsManagement';
import SystemSettings from '@/pages/family/admin/SystemSettings';
import ActivityAdminPage from '@/pages/family/admin/ActivityAdminPage';
import DeviceWizardAdminPage from '@/pages/family/admin/DeviceWizardAdminPage';
import DeviceSetupPage from '@/pages/device/DeviceSetupPage';
// Phase 17: Onboarding
import OnboardingLayout from '@/pages/onboarding/OnboardingLayout';
import Step0Welcome from '@/pages/onboarding/Step0Welcome';
import Step1Role from '@/pages/onboarding/Step1Role';
import Step2Household from '@/pages/onboarding/Step2Household';
import Step3Seniors from '@/pages/onboarding/Step3Seniors';
import Step4Notifications from '@/pages/onboarding/Step4Notifications';
import Step5Done from '@/pages/onboarding/Step5Done';
// Phase 18: Public Onboarding Flow
import PublicOnboardingStart from '@/pages/onboarding/PublicOnboardingStart';
import PublicOnboardingHousehold from '@/pages/onboarding/PublicOnboardingHousehold';
import PublicOnboardingAccount from '@/pages/onboarding/PublicOnboardingAccount';
import PublicOnboardingDevice from '@/pages/onboarding/PublicOnboardingDevice';
import PublicOnboardingSenior from '@/pages/onboarding/PublicOnboardingSenior';
import PublicOnboardingInvite from '@/pages/onboarding/PublicOnboardingInvite';
import PublicOnboardingFinish from '@/pages/onboarding/PublicOnboardingFinish';
// Module Management
import SeniorModulesPage from '@/pages/family/modules/SeniorModulesPage';
// Settings Management
import SeniorSettingsPage from '@/pages/family/settings/SeniorSettingsPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Device Route Guard
 * Ensures device.hayatfamily.app only shows /device routes
 * Redirects any non-device routes to /device/setup
 */
function DeviceRouteGuard({ children }) {
  const location = useLocation();
  const deviceDomain = isDeviceDomain();

  // Log domain detection in development
  if (process.env.NODE_ENV === 'development') {
    console.log('[DeviceRouteGuard] Is Device Domain:', deviceDomain);
    console.log('[DeviceRouteGuard] Current Path:', location.pathname);
  }

  // If on device domain and NOT on a /device route, redirect
  if (deviceDomain && !location.pathname.startsWith('/device')) {
    console.log('[DeviceRouteGuard] Redirecting to /device/setup');
    return <Navigate to="/device/setup" replace />;
  }

  return children;
}

function App() {
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
      // Always set first user if we don't have a current user
      if (response.data.length > 0) {
        if (!currentUser) {
          setCurrentUser(response.data[0]);
        } else {
          // Update currentUser if it exists in the new list
          const updatedUser = response.data.find(u => u.id === currentUser.id);
          if (updatedUser) {
            setCurrentUser(updatedUser);
          } else {
            // Current user was deleted, select first available
            setCurrentUser(response.data[0]);
          }
        }
      } else {
        setCurrentUser(null);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  return (
    <div className="App">
      <UpdateNotification />
      <BrowserRouter>
        <DeviceRouteGuard>
          <DeviceSettingsProvider>
            <AudioPlayerProvider>
              <FamilyAuthProvider>
                <Routes>
            <Route path="/" element={<Navigate to="/households" replace />} />
            <Route
              path="/dashboard"
              element={
                <Dashboard
                  users={users}
                  currentUser={currentUser}
                  setCurrentUser={setCurrentUser}
                  refreshUsers={fetchUsers}
                />
              }
            />
            <Route
              path="/senior/:userId"
              element={<SeniorInterface />}
            />
            <Route
              path="/users"
              element={<UserManagement users={users} refreshUsers={fetchUsers} />}
            />
            {/* Hayat Assistant Routes */}
            <Route path="/households" element={<HouseholdOverview />} />
            <Route path="/households/:householdId" element={<HouseholdDetails />} />
            <Route path="/households/:householdId/seniors/new" element={<SeniorProfileEditor />} />
            <Route path="/seniors/:seniorId" element={<SeniorProfileEditor />} />
            {/* Phase 2 Routes */}
            <Route path="/households/:householdId/family" element={<FamilyUsers />} />
            <Route path="/households/:householdId/activity" element={<ActivityFeed />} />
            {/* Phase 3 Routes */}
            <Route path="/seniors/:seniorId/religion" element={<SeniorReligionSettings />} />
            {/* Phase 4 Routes */}
            <Route path="/device/:deviceId" element={<DeviceHome />} />
            <Route path="/device/setup" element={<DeviceSetupPage />} />
            <Route path="/device" element={<DeviceHome />} />
            <Route path="/households/:householdId/devices/register" element={<DeviceRegistration />} />
            {/* Phase 6 Routes */}
            <Route path="/family/:familyUserId/notifications" element={<NotificationSettings />} />
            <Route path="/households/:householdId/notification-events" element={<NotificationEvents />} />
            
            {/* Phase 11: Family Web-App Routes */}
            <Route path="/family/login" element={<FamilyLogin />} />
            {/* Phase 16: Invitation Routes (public) */}
            <Route path="/family/invite/accept/:token" element={<FamilyInviteAcceptPage />} />
            
            {/* Phase 17: Onboarding Routes (for existing users) */}
            <Route path="/onboarding" element={<OnboardingLayout />}>
              <Route path="step0" element={<Step0Welcome />} />
              <Route path="step1" element={<Step1Role />} />
              <Route path="step2" element={<Step2Household />} />
              <Route path="step3" element={<Step3Seniors />} />
              <Route path="step4" element={<Step4Notifications />} />
              <Route path="step5" element={<Step5Done />} />
              <Route index element={<Navigate to="/onboarding/step0" replace />} />
            </Route>

            {/* Phase 18: Public Onboarding Routes (for new users) */}
            <Route path="/family/onboarding/start" element={<PublicOnboardingStart />} />
            <Route path="/family/onboarding/household" element={<PublicOnboardingHousehold />} />
            <Route path="/family/onboarding/account" element={<PublicOnboardingAccount />} />
            <Route path="/family/onboarding/device" element={<PublicOnboardingDevice />} />
            <Route path="/family/onboarding/senior" element={<PublicOnboardingSenior />} />
            <Route path="/family/onboarding/invite" element={<PublicOnboardingInvite />} />
            <Route path="/family/onboarding/finish" element={<PublicOnboardingFinish />} />
            
            <Route path="/family" element={<FamilyLayout />}>
              <Route path="dashboard" element={<FamilyDashboard />} />
              <Route path="modules" element={<FamilyModules />} />
              <Route path="senioren" element={<FamilySenioren />} />
              <Route path="seniors" element={<FamilySenioren />} />
              <Route path="seniors/:seniorId" element={<FamilySeniorDetail />} />
              <Route path="seniors/:seniorId/call" element={<FamilyVideoCall />} />
              <Route path="seniors/:seniorId/contacts" element={<ContactsManagementPage />} />
              <Route path="seniors/:seniorId/modules" element={<SeniorModulesPage />} />
              <Route path="seniors/:seniorId/device-settings" element={<SeniorSettingsPage />} />
              <Route path="incoming-call/:sessionId" element={<FamilyIncomingVideoCall />} />
              <Route path="activity" element={<FamilyActivity />} />
              <Route path="notifications" element={<FamilyNotifications />} />
              
              {/* Phase 19: New Admin Routes (Protected) */}
              <Route path="admin/members" element={<RequireAdmin><MembersManagement /></RequireAdmin>} />
              <Route path="admin/invite" element={<RequireAdmin><InviteMember /></RequireAdmin>} />
              <Route path="admin/seniors" element={<RequireAdmin><SeniorsManagement /></RequireAdmin>} />
              <Route path="admin/settings" element={<RequireAdmin><SystemSettings /></RequireAdmin>} />
              <Route path="admin/activity" element={<RequireAdmin><ActivityAdminPage /></RequireAdmin>} />
              <Route path="admin/devices/wizard" element={<RequireAdmin><DeviceWizardAdminPage /></RequireAdmin>} />
              
              {/* Redirect legacy activity route */}
              <Route path="activity" element={<Navigate to="/family/admin/activity" replace />} />
              
              {/* Phase 16: Legacy Admin Management (keep for backward compatibility) */}
              <Route path="invite" element={<FamilyInvitePage />} />
              <Route path="settings" element={<HouseholdSettingsPage />} />
              <Route path="members" element={<MemberManagement />} />
              <Route path="seniors/:seniorId/contacts" element={<SeniorContactsPage />} />
              
              <Route index element={<Navigate to="/family/dashboard" replace />} />
            </Route>
                </Routes>
              </FamilyAuthProvider>
            </AudioPlayerProvider>
          </DeviceSettingsProvider>
        </DeviceRouteGuard>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;